import { z } from 'zod';

/**
 * Invite & Referral Schemas
 * Invite links, email/wallet invites, and referral tracking
 */

// Invite Status Enum
export const InviteStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED'], {
  error: "Invalid invite status"
});

// Invite Type Enum
export const InviteTypeSchema = z.enum(['LINK', 'EMAIL', 'WALLET'], {
  error: "Invalid invite type"
});

// Core Invite Entity
export const InviteSchema = z.object({
  id: z.string(),
  type: InviteTypeSchema,
  email: z.string().email().optional(),
  walletAddress: z.string().optional(),
  code: z.string(),
  linkSlug: z.string().optional(),
  status: InviteStatusSchema,
  invitedById: z.string(),
  acceptedById: z.string().optional(),
  role: z.string().default('user'),
  message: z.string().optional(),
  expiresAt: z.coerce.date(),
  usedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- API Request Schemas ---

// Create invite link (no params needed, generates a link)
export const CreateInviteLinkSchema = z.object({});

// Send email invites
export const SendEmailInvitesSchema = z.object({
  emails: z.array(z.string().email()).min(1).max(10),
  message: z.string().optional(),
});

// Send wallet invites
export const SendWalletInvitesSchema = z.object({
  walletAddresses: z.array(z.string()).min(1).max(10),
  message: z.string().optional(),
});

// Accept an invite
export const AcceptInviteSchema = z.object({
  code: z.string(),
});

// --- API Response Schemas ---

// Invite link response
export const InviteLinkResponseSchema = z.object({
  id: z.string(),
  url: z.string(),
  linkSlug: z.string(),
  createdAt: z.coerce.date(),
});

// Referral entry
export const ReferralSchema = z.object({
  id: z.string(),
  inviteCode: z.string(),
  source: InviteTypeSchema,
  status: InviteStatusSchema,
  earnings: z.number(),
  acceptedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
});

// Invite list response
export const InviteListResponseSchema = z.object({
  links: z.array(InviteLinkResponseSchema),
  referrals: z.array(ReferralSchema),
  totalInvites: z.number(),
  totalAccepted: z.number(),
});

// Export TypeScript types
export type InviteStatus = z.infer<typeof InviteStatusSchema>;
export type InviteType = z.infer<typeof InviteTypeSchema>;
export type Invite = z.infer<typeof InviteSchema>;
export type CreateInviteLink = z.infer<typeof CreateInviteLinkSchema>;
export type SendEmailInvites = z.infer<typeof SendEmailInvitesSchema>;
export type SendWalletInvites = z.infer<typeof SendWalletInvitesSchema>;
export type AcceptInvite = z.infer<typeof AcceptInviteSchema>;
export type InviteLinkResponse = z.infer<typeof InviteLinkResponseSchema>;
export type Referral = z.infer<typeof ReferralSchema>;
export type InviteListResponse = z.infer<typeof InviteListResponseSchema>;
